import { useState, useEffect } from "react";
import { useSession } from "next-auth/client";
import {
  Text,
  Flex,
  Box,
  Image,
  Link,
  theme,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Textarea,
} from "@chakra-ui/react";
import numbro from "numbro";
import styled from "@emotion/styled";
import twitter from "twitter-text";
import toast from "react-hot-toast";
import { useFirebaseUser } from "utils/useFirebaseUser";
import { firebaseClient } from "firebaseClient";
import { getAccount } from "utils/sessionHelper";
import { SaveIcon } from "components/icons/SaveIcon";
import { layerStyle } from "theme/names";
import { EditIconSVG } from "components/icons/EditIconSVG";

export interface AccountInfoProps {
  id_str: string;
  name: string;
  username: string;
  image: string;
  followers_count: number;
  bioFormatted: string;
  bio: string;
}

interface Props extends AccountInfoProps {
  editMode?: boolean;
}

export function AccountInfo({
  name,
  username,
  image,
  followers_count,
  bioFormatted,
  bio,
  editMode = false,
}: Props) {
  const [description, setDescription] = useState<string>("");
  const [descriptionFormatted, setDescriptionFormatted] = useState<string>("");
  const [tempBio, setTempBio] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [session] = useSession();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isAuthenticated = useFirebaseUser();

  useEffect(() => {
    setDescription(bio);
    setTempBio(bio);
    setDescriptionFormatted(bioFormatted);
  }, [bioFormatted, bio]);

  const handleUpdate = async () => {
    try {
      if (!session && !isAuthenticated) {
        toast.error("User is not authenticated");
        return;
      }

      setIsLoading(true);
      const formatted = twitter.autoLink(tempBio, {
        usernameIncludeSymbol: true,
        targetBlank: true,
      });

      const db = firebaseClient.firestore();
      await db
        .collection("users")
        .doc(getAccount(session)?.id)
        .collection("accounts")
        .doc(getAccount(session)?.idAccount)
        .update({ bio: tempBio, bioFormatted: formatted });

      setDescription(tempBio);
      setDescriptionFormatted(formatted);
      bioFormatted = formatted;
      bio = tempBio;
      setIsLoading(false);
      onClose();
    } catch (err) {
      console.log("Error in updating bio: ", err);
      toast.error("Error in updating bio: " + err.message);
    }
  };

  return (
    <Flex w="100%" mb="10" flexDir="column" alignItems="center" layerStyle={layerStyle["bg.border.rounded"]} p="8" pt="10">
      <Flex>
        <Box>
          <Image
            width={20}
            height={20}
            borderRadius={"full"}
            ml={0}
            src={image}
            fallbackSrc="/assets/resources/emptyProfile.png"
          />
        </Box>
        <Flex flexDirection="column" ml={5} mt={0} position="relative">
          <Text maxW="180px" noOfLines={1} wordBreak="break-all" textStyle="h4.bold.standard">
            {name}
          </Text>
          <Link
            href={"https://twitter.com/" + username}
            target="_blank"
            // color="gray.400"
            // fontWeight="400"
            maxW="180px"
            noOfLines={1} wordBreak="break-all"
            textStyle="body.medium.light"
          >
            <Text textStyle="body.medium.light">
              @{username}
            </Text>
          </Link>
          <Flex mt={.5}>
            <Text maxW="180px" noOfLines={1} wordBreak="break-all" textStyle="body.medium.light">
              {numbro(followers_count).format({ average: true })}
            </Text>
            <Text
              textStyle="body.medium.light"
              // ml={2}
              // color="gray.400"
              // fontWeight="400"
              maxW="180px"
              noOfLines={1} wordBreak="break-all"
            >
              {` ${followers_count === 1 ? "Follower" : "Followers"}`}
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <Box mt="5">
        <StyledText
          align="center"
          whiteSpace="pre-wrap"
          dangerouslySetInnerHTML={{
            __html: descriptionFormatted ?? description,
          }}
          display="inline"
        />
        {editMode && (
          <>
            {!descriptionFormatted && !description && (
              <Text display="inline" textStyle="body.medium.light" textAlign="center">
                [Add description]
              </Text>
            )}
            <IconButton
              aria-label="edit description"
              icon={EditIconSVG}
              variant="tertiary"
              size="sm"
              ml="2"
              onClick={onOpen}
            />
          </>
        )}
      </Box>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setTempBio(bio);
          onClose();
        }}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit description</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Textarea
              h="150px"
              value={tempBio}
              onChange={(e) => setTempBio(e.target.value)}
            />
          </ModalBody>

          <ModalFooter>
            <Button onClick={onClose} mr={3}>Cancel</Button>
            <Button
              variant={"primary"}
              mr={3}
              onClick={handleUpdate}
              isLoading={isLoading}
              leftIcon={SaveIcon}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

const StyledText = styled(Text)`
  a {
    color: ${() => theme.colors.blue[400]};
  }
`;
